import { generateQueryStringFromParams } from '@/utils/history';
import { ApiTagTypes, innovorderApi } from '../index';
import { ApiResponse } from '../request.types';
import {
    DeleteProductPayload,
    DeleteProductResponse,
    GetProductsPayload,
    GetProductsResponse,
    GetProductPayload,
    GetProductResponse,
    UpdateProductResponse,
    UpdateProductPayload,
    CreateProductPayload,
    CreateProductResponse,
    DuplicateProductPayload,
    DuplicateProductResponse,
    MassProductUpdatePayload,
    MassProductUpdateResponse,
} from './products.type';

const productsApi = innovorderApi.injectEndpoints({
    endpoints: (build) => ({
        getProducts: build.query<GetProductsResponse, GetProductsPayload>({
            query: ({ menuId, step_exclusion, limit, offset }) => {
                const queryParameters = generateQueryStringFromParams({ limit, offset, step_exclusion });

                return {
                    url: `/menus/${menuId}/products?${queryParameters}`,
                    method: 'GET',
                };
            },
            providesTags: (response, _, queryArgs) => {
                if (response) {
                    return [
                        ...response.map(({ signature }) => ({
                            type: ApiTagTypes.Product,
                            id: `${queryArgs.menuId}-${signature}`,
                        })),
                        { type: ApiTagTypes.Product, id: `${queryArgs.menuId}-LIST` },
                    ];
                }
                return [{ type: ApiTagTypes.Product, id: 'LIST' }];
            },
            transformResponse: (response: ApiResponse<GetProductsResponse>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        getProduct: build.query<GetProductResponse, GetProductPayload>({
            query: ({ menuId, signature }) => ({
                url: `/menus/${menuId}/products/${signature}`,
                method: 'GET',
            }),
            providesTags: (_, __, queryArgs) => {
                return [
                    { type: ApiTagTypes.Product, id: `${queryArgs.menuId}-${queryArgs.signature}` },
                    { type: ApiTagTypes.Product, id: `${queryArgs.menuId}-LIST` },
                ];
            },
            transformResponse: (response: ApiResponse<GetProductResponse>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        updateProduct: build.mutation<UpdateProductResponse, UpdateProductPayload>({
            query: ({ menuId, signature, ...body }) => {
                return {
                    url: `/menus/${menuId}/products/${signature}`,
                    method: 'PUT',
                    body,
                };
            },

            invalidatesTags: (_, __, queryArgs) => {
                return [
                    { type: ApiTagTypes.Product, id: `${queryArgs.menuId}-${queryArgs.signature}` },
                    { type: ApiTagTypes.Product, id: `${queryArgs.menuId}-LIST` },
                    { type: ApiTagTypes.Sku, id: `${queryArgs.menuId}-LIST` },
                    { type: ApiTagTypes.Category, id: `${queryArgs.menuId}-LIST` },
                    { type: ApiTagTypes.Steps, id: `${queryArgs.menuId}-LIST` },
                ];
            },
        }),
        createProduct: build.mutation<CreateProductResponse, CreateProductPayload>({
            query: (body) => {
                return {
                    url: `/menus/${body.menuId}/products`,
                    method: 'POST',
                    body,
                };
            },
            invalidatesTags: (_, __, queryArgs) => {
                return [
                    { type: ApiTagTypes.Product, id: `${queryArgs.menuId}-LIST` },
                    { type: ApiTagTypes.Sku, id: `${queryArgs.menuId}-LIST` },
                ];
            },
        }),
        deleteProduct: build.mutation<DeleteProductResponse, DeleteProductPayload>({
            query: ({ menuId, signature }) => {
                return {
                    url: `/menus/${menuId}/products/${signature}`,
                    method: 'DELETE',
                };
            },
            invalidatesTags: (_, __, queryArgs) => {
                return [
                    { type: ApiTagTypes.Product, id: `${queryArgs.menuId}-${queryArgs.signature}` },
                    { type: ApiTagTypes.Product, id: `${queryArgs.menuId}-LIST` },
                ];
            },
        }),
        duplicateProduct: build.mutation<DuplicateProductResponse, DuplicateProductPayload>({
            query: ({ menuId, signature }) => {
                return {
                    url: `/menus/${menuId}/products/${signature}/duplicate`,
                    method: 'POST',
                };
            },
            invalidatesTags: (_, __, queryArgs) => {
                return [
                    { type: ApiTagTypes.Product, id: `${queryArgs.menuId}-LIST` },
                    { type: ApiTagTypes.Sku, id: `${queryArgs.menuId}-LIST` },
                ];
            },
        }),
        massProductUpdate: build.mutation<MassProductUpdateResponse, MassProductUpdatePayload>({
            query: ({ menuId, ...body }) => {
                return {
                    url: `/menus/${menuId}/products`,
                    method: 'PATCH',
                    body,
                };
            },
            invalidatesTags: (_, __, { menuId }) => {
                return [
                    { type: ApiTagTypes.Product, id: `${menuId}-LIST` },
                    { type: ApiTagTypes.Steps, id: `${menuId}-LIST` },
                    { type: ApiTagTypes.Tag, id: `${menuId}-LIST` },
                    { type: ApiTagTypes.Sku, id: `${menuId}-LIST` },
                ];
            },
        }),
    }),
});

export const {
    useCreateProductMutation,
    useDeleteProductMutation,
    useDuplicateProductMutation,
    useGetProductQuery,
    useGetProductsQuery,
    useMassProductUpdateMutation,
    useUpdateProductMutation,
} = productsApi;
